import React, { useState, useEffect } from 'react'
import { RefreshCw, MessageSquare, ThumbsUp, ThumbsDown, Eye, Trash2, Check } from 'lucide-react'
import { feedbackService } from '../services/feedbackService'
import { Feedback, FeedbackStats } from '../types/feedback'

/**
 * FeedbackAdmin - Admin interface for managing user feedback
 * Following Design Principles: Visual Hierarchy, Readability, White Space, Consistency
 */
const FeedbackAdmin: React.FC = () => {
  const [feedback, setFeedback] = useState<Feedback[]>([])
  const [stats, setStats] = useState<FeedbackStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<string>('all')

  const fetchData = async () => {
    setLoading(true)
    try {
      const [feedbackData, statsData] = await Promise.all([
        feedbackService.getAllFeedback(filter !== 'all' ? { status: filter } : {}),
        feedbackService.getStats()
      ])
      setFeedback(feedbackData)
      setStats(statsData)
    } catch (error) {
      console.error('Failed to fetch feedback:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [filter])

  const handleStatusUpdate = async (id: string, status: string) => {
    try {
      await feedbackService.updateStatus(id, status)
      await fetchData()
    } catch (error) {
      alert('Failed to update status')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this feedback?')) return

    try {
      await feedbackService.deleteFeedback(id)
      await fetchData()
    } catch (error) {
      alert('Failed to delete feedback')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Feedback Admin</h1>
              <p className="text-gray-500 mt-1">Review user feedback and suggestions</p>
            </div>
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {/* Total */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <MessageSquare className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
              </div>
            </div>

            {/* Positive */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <ThumbsUp className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Positive</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.positive}</p>
                </div>
              </div>
            </div>

            {/* Negative */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <ThumbsDown className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Negative</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.negative}</p>
                </div>
              </div>
            </div>

            {/* Pending */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Eye className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex gap-2">
            {['all', 'new', 'reviewed', 'resolved'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === status
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Feedback List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Loading feedback...</p>
            </div>
          ) : feedback.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-500">No feedback found</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {feedback.map((item) => (
                <div key={item.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {/* Rating + Meta */}
                      <div className="flex items-center gap-3 mb-2">
                        {item.rating === 'positive' && (
                          <ThumbsUp className="w-5 h-5 text-green-600" />
                        )}
                        {item.rating === 'negative' && (
                          <ThumbsDown className="w-5 h-5 text-red-600" />
                        )}
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          item.status === 'new' ? 'bg-blue-100 text-blue-700' :
                          item.status === 'reviewed' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {item.status}
                        </span>
                        <span className="text-sm text-gray-500">
                          {new Date(item.created_at).toLocaleString()}
                        </span>
                        {item.source_language && item.target_language && (
                          <span className="text-sm text-gray-600">
                            {item.source_language} → {item.target_language}
                          </span>
                        )}
                      </div>

                      {/* Message */}
                      <p className="text-gray-900 mb-2">{item.message}</p>

                      {/* Page URL */}
                      {item.page_url && (
                        <a
                          href={item.page_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline"
                        >
                          {item.page_url}
                        </a>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {item.status === 'new' && (
                        <button
                          onClick={() => handleStatusUpdate(item.id, 'reviewed')}
                          className="p-2 text-yellow-600 hover:bg-yellow-50 rounded transition-colors"
                          title="Mark as reviewed"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      )}
                      {item.status !== 'resolved' && (
                        <button
                          onClick={() => handleStatusUpdate(item.id, 'resolved')}
                          className="p-2 text-green-600 hover:bg-green-50 rounded transition-colors"
                          title="Mark as resolved"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default FeedbackAdmin
